import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Building2, Mail } from "lucide-react";
import { PhoneInput, OtpInput } from "@/components/auth";
import { AuthShell } from "@/components/auth/AuthShell";
import { ForgotPasswordForm } from "@/components/auth/ForgotPasswordForm";
import { useDevOtp } from "@/hooks/useDevOtp";
import { OTP_LENGTH } from "@/components/auth/OtpInput";
import { isValidSaudiPhone, toE164Phone } from "@/lib/phone";
import { usePortalRouter } from "@/hooks/usePortalRouter";
import { NafathVerificationModal } from "@/components/nafath/NafathVerificationModal";
import { nestRequestOtp, nestVerifyOtp, nestLoginPassword, nestSignOut, NestApiError } from "@/services/api";
import { ConsentCheckboxes } from "@/components/auth/ConsentCheckboxes";
import type { NafathIdentityData } from "@/services/api";
import { persistNestRefreshToken } from "@/lib/authHelpers";

type AuthStep = "phone" | "otp" | "new_user" | "nafath" | "email_login" | "forgot_password";

export default function Auth() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { getPostLoginPath } = usePortalRouter();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  // Single-flight guard for `handleVerifyOtp` — prevents a double-fire from
  // OtpInput's auto-fill paths (devOtp + Web OTP Credential) from sending
  // two `/auth/verify-otp-d1` requests. The first request consumes the
  // OTP server-side; the second would land on a now-empty slot and surface
  // auth-004 ("No active OTP found for this phone number") AFTER the user
  // already succeeded. `isLoading` can't carry this guarantee — React 18
  // batches setState across microtask boundaries, so a synchronous double-
  // call sees `isLoading=false` both times. A ref bypasses batching.
  const verifyingRef = useRef(false);

  // Invitation context from URL params (kept for backwards-compatible invite
  // links that pointed at /auth?invite=...; new admin-invite flow handles
  // everything inside /admin-invite/:token).
  const invitePhone = searchParams.get("phone");

  // Auth state
  // ?mode=email / ?mode=forgot-password deep-link into the email steps (used by
  // the /auth/reset-password page's "log in" and "request a new link" buttons).
  const mode = searchParams.get("mode");
  const [step, setStep] = useState<AuthStep>(
    mode === "email" ? "email_login" : mode === "forgot-password" ? "forgot_password" : "phone",
  );
  const [phoneNumber, setPhoneNumber] = useState(import.meta.env.VITE_DEV_PHONE || "");
  const [otp, setOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [otpKey, setOtpKey] = useState(0);
  const [otpSentCount, setOtpSentCount] = useState(0);

  const devOtp = useDevOtp(step === "otp" ? phoneNumber : null, otpSentCount);

  // PDPL Art. 5–6 — explicit consent checkbox for the new_user (signup) step only.
  // Resets when the user leaves the new_user step.
  const [signupConsentChecked, setSignupConsentChecked] = useState(false);

  // Email login state
  const [emailLogin, setEmailLogin] = useState("");
  const [passwordLogin, setPasswordLogin] = useState("");

  // Nafath enforcement state
  const [loggedInUserId, setLoggedInUserId] = useState<string>("");
  const [loggedInCompanyId, setLoggedInCompanyId] = useState<string>("");
  const [postLoginPath, setPostLoginPath] = useState("/dashboard");

  // Check if user is already logged in — redirect to their portal based on role
  useEffect(() => {
    const redirectLoggedInUser = async (userId: string) => {
      const { data: roleRows } = await supabase
        .from("user_roles")
        .select("role, company_id")
        .eq("user_id", userId);
      const roleStrings = (roleRows ?? []).map((r: { role: string }) => r.role);

      // Check junction-table assignments: a custom role with portal_type='admin'
      // should route to the admin dashboard (as 'manager') instead of employee portal.
      const companyId = roleRows?.[0]?.company_id;
      if (companyId) {
        try {
          const { data: assignments } = await supabase
            .from("user_role_assignments")
            .select("role_id, role_permissions!inner(portal_type)")
            .eq("user_id", userId)
            .eq("company_id", companyId);

          const hasAdminPortal = assignments?.some(
            (a) => a.role_permissions?.portal_type === "admin",
          );
          if (
            hasAdminPortal &&
            !roleStrings.includes("admin") &&
            !roleStrings.includes("manager")
          ) {
            roleStrings.push("manager");
          }
        } catch {
          // Non-critical — fallback to system roles only
        }
      }

      const path = roleStrings.length > 0 ? getPostLoginPath(roleStrings) : "/dashboard";
      navigate(path);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        // Defer Supabase calls OUT of the auth-state callback. Awaiting a
        // supabase query directly inside onAuthStateChange deadlocks GoTrue's
        // auth lock: the nest-provider login calls setSession() (which fires
        // SIGNED_IN while holding the lock), and redirectLoggedInUser() would
        // then query inside that held lock and never resolve — leaving login
        // stuck on the spinner. setTimeout breaks out of the lock scope.
        if (session?.user) {
          const uid = session.user.id;
          setTimeout(() => { void redirectLoggedInUser(uid); }, 0);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        // For JWT clock skew errors, rely on auto-refresh instead of signing out
        if (error.message.includes('JWT issued at future')) {
          console.warn('JWT clock skew detected, relying on auto-refresh:', error.message);
          return;
        }
        console.warn('Session retrieval failed:', error.message);
        // Involuntary (session retrieval failed) — local-only; don't revoke other devices.
        nestSignOut({ scope: 'local' });
        return;
      }
      if (session?.user) {
        await redirectLoggedInUser(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, getPostLoginPath]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Reset signup consent checkbox when navigating away from the new_user step
  useEffect(() => {
    if (step !== "new_user") {
      setSignupConsentChecked(false);
    }
  }, [step]);

  // Pre-fill phone from invitation params
  useEffect(() => {
    if (invitePhone) {
      const decodedPhone = decodeURIComponent(invitePhone);
      setPhoneNumber(toE164Phone(decodedPhone) || decodedPhone);
    }
  }, [invitePhone]);

  const validatePhone = () => {
    if (!isValidSaudiPhone(phoneNumber)) {
      toast.error(t("يجب أن يبدأ رقم الجوال بـ 5 ويتكون من 9 أرقام", "Phone must start with 5 and be 9 digits"));
      return false;
    }
    return true;
  };

  const handleSendOtp = async () => {
    if (!validatePhone()) return;
    setIsLoading(true);
    try {
      const { data: registrationData, error: regError } = await supabase
        .rpc("check_phone_registration", { phone_number: phoneNumber });

      if (regError) {
        console.error("[Auth] check_phone_registration RPC error:", regError.message, regError.code);
        toast.error(t("حدث خطأ في الاتصال، حاول مرة أخرى", "Connection error, please try again"));
        setIsLoading(false);
        return;
      }

      if (!registrationData || typeof registrationData !== 'object' || !('found' in registrationData)) {
        console.error("[Auth] Unexpected registration response:", registrationData);
        toast.error(t("هذا الرقم غير مسجل", "This number is not registered"));
        setIsLoading(false);
        return;
      }

      const registration = registrationData as { found: boolean; roles: string[] };

      if (!registration.found) {
        // Phone is not registered — present new-user options instead of blocking
        setStep("new_user");
        setIsLoading(false);
        return;
      }

      // D4: GoTrue is decommissioned — OTP send always goes through the Nest
      // session authority (D1). The registration check above already gated
      // unknown phones (mirroring the old shouldCreateUser:false).
      let res: { success: boolean; message: string };
      try {
        res = await nestRequestOtp(phoneNumber);
      } catch (otpErr: unknown) {
        // nestRequestOtp throws when the API returns non-2xx. Detect rate-limit
        // (HTTP 429) by the status carried on NestApiError and surface a clear
        // bilingual message; otherwise show a generic connection error — never
        // expose raw "HTTP Client Error" strings. (The NestJS throttler returns
        // the English "ThrottlerException: Too Many Requests" body, so matching
        // the status is reliable where substring matching the message was not.)
        console.error("[Auth] nestRequestOtp threw:", otpErr);
        if (otpErr instanceof NestApiError && otpErr.status === 429) {
          toast.error(t("يرجى الانتظار قبل إعادة الطلب", "Please wait before requesting again"));
        } else {
          toast.error(t("فشل إرسال رمز التحقق، حاول مرة أخرى", "Failed to send verification code, please try again"));
        }
        return;
      }
      if (!res.success) {
        console.error("[Auth] nest request-otp failed:", res.message);
        toast.error(t("فشل إرسال رمز التحقق", "Failed to send verification code"));
        setIsLoading(false);
        return;
      }

      setStep("otp");
      setResendTimer(60);
      setOtpSentCount((c) => c + 1);
      toast.success(t("تم إرسال رمز التحقق", "Verification code sent"));
    } catch (error: unknown) {
      // Outer catch: handles unexpected errors from check_phone_registration RPC
      // (e.g. network failure routed through withActiveRoleHeader) or any other
      // unhandled throw. Never surface raw HTTP error strings to the user — the
      // "HTTP Client Error with status code: 500" that was showing up in Sentry
      // (DALLALI-FRONTEND-9Z / issue #1422) came from this path.
      console.error("[Auth] handleSendOtp unexpected error:", error);
      toast.error(t("حدث خطأ في الاتصال، حاول مرة أخرى", "Connection error, please try again"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = () => {
    if (resendTimer > 0) return;
    setOtp("");
    setOtpKey((k) => k + 1);
    handleSendOtp();
  };

  const handleVerifyOtp = async (otpValue?: string) => {
    // Drop re-entrant calls before any work happens. Both OtpInput's
    // explicit autofill onComplete AND input-otp's internal auto-fire can
    // reach here in the same React render cycle — this is the seam that
    // closes the auth-004 hole at the consumer rather than fighting the
    // library upstream.
    if (verifyingRef.current) return;
    const code = otpValue || otp;
    if (code.length !== OTP_LENGTH) {
      toast.error(t("يرجى إدخال رمز التحقق كاملاً", "Please enter the complete verification code"));
      return;
    }
    verifyingRef.current = true;
    setIsLoading(true);
    try {
      // D4: verify via the Nest session authority (D1), then inject the minted
      // Supabase-compatible JWT into the existing client via setSession() so
      // Storage / Realtime / PostgREST keep working on the Nest-minted token.
      let tokens;
      try {
        tokens = await nestVerifyOtp(phoneNumber, code);
      } catch (verifyErr: unknown) {
        console.error("[Auth] nest verify-otp failed:", verifyErr);
        // Map the API error to a specific bilingual user message. The Nest auth
        // controller returns { code, ar, en, message } where `message` === the
        // Arabic `ar` field, and nestVerifyOtp throws with error.message = that
        // Arabic text — so we match on the Arabic keywords (the `auth-00x` codes
        // never appear in the message and were dead branches).
        const errMsg = verifyErr instanceof Error ? verifyErr.message : String(verifyErr);
        if (errMsg.includes("انتهت صلاحية")) {
          toast.error(t("انتهت صلاحية الرمز، يرجى طلب رمز جديد", "Code expired — please request a new one"));
        } else if (errMsg.includes("الحد الأقصى")) {
          toast.error(t("تم تجاوز الحد الأقصى للمحاولات، يرجى طلب رمز جديد", "Too many attempts — please request a new code"));
        } else if (errMsg.includes("لا يوجد رمز")) {
          toast.error(t("لا يوجد رمز نشط، يرجى طلب رمز جديد", "No active code — please request a new one"));
        } else {
          // auth-002 OTP_INVALID (wrong code) or any other verify failure
          toast.error(t("الرمز غير صحيح، يرجى التحقق والمحاولة مجدداً", "Incorrect code — please check and try again"));
        }
        return;
      }

      const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
      });
      if (sessionError || !sessionData.session) {
        console.error("[Auth] setSession failed:", sessionError?.message);
        toast.error(t("فشل تسجيل الدخول", "Login failed"));
        setIsLoading(false);
        return;
      }

      // Cache the Nest refresh token so useAuth can recover/rotate the session.
      persistNestRefreshToken(tokens.refresh_token);
      const userId = sessionData.user?.id;

      if (!userId) {
        toast.error(t("فشل تسجيل الدخول", "Login failed"));
        setIsLoading(false);
        return;
      }

      toast.success(t("تم تسجيل الدخول بنجاح", "Login successful"));

      // Fetch roles + profile in parallel (both independent queries)
      const [{ data: loginRoles }, { data: profile }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", userId),
        supabase.from("profiles").select("nafath_verified, company_id").eq("id", userId).maybeSingle(),
      ]);

      const roleStrings = (loginRoles ?? []).map(r => r.role);
      const resolvedPath = getPostLoginPath(roleStrings);

      // Nafath verification must run BEFORE admin-invite activation so that
      // profile.nafath_verified is true when rpc_accept_admin_invitation runs.
      // This also prevents orphan companies if the user abandons at Nafath.
      if (!profile?.nafath_verified) {
        setLoggedInUserId(userId);
        setLoggedInCompanyId(profile?.company_id || "");
        setPostLoginPath(resolvedPath);
        setStep("nafath");
        setIsLoading(false);
        return;
      }

      localStorage.setItem("dallali-pwa-show-install-modal", "true");
      navigate(resolvedPath);
    } catch (_error: unknown) {
      // Outer catch: handles unexpected errors from Promise.all (roles + profile
      // queries) or navigation. Never surface raw error strings to the user.
      console.error("[Auth] handleVerifyOtp unexpected error:", _error);
      toast.error(t("حدث خطأ، يرجى المحاولة مجدداً", "An error occurred, please try again"));
    } finally {
      verifyingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async () => {
    if (!emailLogin || !passwordLogin) {
      toast.error(t("يرجى إدخال البريد الإلكتروني وكلمة المرور", "Please enter email and password"));
      return;
    }
    // Validate email format before hitting the API — prevents the NestJS DTO
    // validation error "email must be a valid email address" from reaching Sentry
    // (DALLALI-FRONTEND-BE / issue #2166).
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailLogin.trim())) {
      toast.error(t("يرجى إدخال بريد إلكتروني صحيح", "Please enter a valid email address"));
      return;
    }
    setIsLoading(true);
    try {
      // D4: route email/password through the Nest session authority (D1), then
      // inject the minted Supabase-compatible JWT via setSession() — same as the
      // OTP path. This gives email/password users an app.nest_sessions row so the
      // Nest refresh loop can rotate their session.
      let tokens;
      try {
        tokens = await nestLoginPassword(emailLogin.trim(), passwordLogin);
      } catch (loginErr: unknown) {
        console.error("[Auth] nest password login failed:", loginErr);
        toast.error(t("البريد الإلكتروني أو كلمة المرور غير صحيحة", "Invalid email or password"));
        return;
      }

      const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
      });
      if (sessionError || !sessionData.session) {
        console.error("[Auth] setSession failed:", sessionError?.message);
        toast.error(t("فشل تسجيل الدخول", "Login failed"));
        setIsLoading(false);
        return;
      }

      // Cache the Nest refresh token so useAuth can recover/rotate the session.
      persistNestRefreshToken(tokens.refresh_token);
      const userId = sessionData.user?.id;

      if (!userId) {
        toast.error(t("فشل تسجيل الدخول", "Login failed"));
        setIsLoading(false);
        return;
      }

      toast.success(t("تم تسجيل الدخول بنجاح", "Login successful"));

      const [{ data: loginRoles }, { data: profile }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", userId),
        supabase.from("profiles").select("nafath_verified, company_id").eq("id", userId).maybeSingle(),
      ]);

      const roleStrings = (loginRoles ?? []).map(r => r.role);
      const resolvedPath = getPostLoginPath(roleStrings);

      if (!profile?.nafath_verified) {
        setLoggedInUserId(userId);
        setLoggedInCompanyId(profile?.company_id || "");
        setPostLoginPath(resolvedPath);
        setStep("nafath");
        setIsLoading(false);
        return;
      }

      localStorage.setItem("dallali-pwa-show-install-modal", "true");
      navigate(resolvedPath);
    } catch (_error: unknown) {
      // Outer catch: handles unexpected errors from Promise.all (roles + profile
      // queries) or navigation after email login. Never surface raw strings.
      console.error("[Auth] handleEmailLogin unexpected error:", _error);
      toast.error(t("حدث خطأ، يرجى المحاولة مجدداً", "An error occurred, please try again"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNafathVerified = async (
    identity: NafathIdentityData | null,
    nationalId: string
  ) => {
    if (!loggedInUserId) return;

    const arabicName = identity
      ? [identity.firstName, identity.secondName, identity.thirdName, identity.lastName]
          .filter(Boolean).join(" ")
      : undefined;
    const englishName = identity
      ? [identity.englishFirstName, identity.englishSecondName, identity.englishThirdName, identity.englishLastName]
          .filter(Boolean).join(" ")
      : undefined;

    // One identity-write path: applies the verified identity to profiles AND the
    // linked owner/tenant directory rows atomically (so re-verify no longer heals
    // only the profile — the desync root cause). Login re-verify is authoritative,
    // so we pass the Nafath name; the RPC leaves a field unchanged when null.
    // Cast: the RPC is newer than the auto-generated supabase types.
    // TODO: drop the `as never` casts once types.ts is regenerated post-deploy.
    const { data, error } = await supabase.rpc("apply_verified_identity" as never, {
      p_user_id: loggedInUserId,
      p_full_name_ar: arabicName ?? null,
      p_full_name_en: englishName ?? null,
      p_national_id: nationalId,
    } as never);

    if (error || !(data as { success?: boolean } | null)?.success) {
      toast.error(t("فشل حفظ بيانات التحقق", "Failed to save verification data"));
      return;
    }

    localStorage.setItem("dallali-pwa-show-install-modal", "true");
    navigate(postLoginPath);
  };

  const handleBack = () => {
    setStep("phone");
    setOtp("");
  };

  return (
    <>
      <AuthShell>
            {step === "phone" ? (
              <>
                {/* Header */}
                <div className="space-y-2 w-full !text-center">
                  <h1 className="text-2xl font-bold text-white !text-center" data-testid="welcome-heading">
                    {t("أهلاً بعودتك", "Welcome Back")}
                  </h1>
                  <p className="text-white/60 text-sm !text-center">
                    {t(
                      "ادخل بياناتك لمتابعة تسجيل الدخول إلى حسابك",
                      "Enter your details to continue logging in to your account"
                    )}
                  </p>
                </div>

                {/* Phone field */}
                <div className="space-y-2 w-full text-center" data-testid="phone-input">
                  <PhoneInput
                    value={phoneNumber}
                    onChange={(fullNumber) => setPhoneNumber(fullNumber)}
                    disabled={isLoading}
                    autoFocus
                    onEnter={handleSendOtp}
                    className="[&_button]:bg-white/10 [&_button]:border-white/20 [&_button]:text-white [&_input]:bg-white/10 [&_input]:border-white/20 [&_input]:text-white [&_input]:placeholder-white/40"
                  />
                </div>

                {/* Login button */}
                <Button
                  onClick={handleSendOtp}
                  className="w-full bg-[#791FDA] hover:bg-[#6a18c7] text-white"
                  disabled={isLoading}
                  size="lg"
                  data-testid="login-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="me-2 h-4 w-4 animate-spin" />
                      {t("جاري الإرسال...", "Sending...")}
                    </>
                  ) : (
                    t("تسجيل الدخول", "Login")
                  )}
                </Button>


                {/* Email login link */}
                <button
                  type="button"
                  onClick={() => setStep("email_login")}
                  className="text-sm text-[#791FDA] hover:underline flex items-center gap-1.5 mx-auto"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {t("تسجيل الدخول بالبريد الإلكتروني", "Login with email")}
                </button>

                {/* PDPL Art. 5–6 — passive informational notice (not a blocking checkbox) */}
                <p className="text-xs text-white/50 leading-relaxed text-start">
                  {t("بالمتابعة، أنت توافق على ", "By continuing, you agree to our ")}{" "}
                  <Link to="/privacy-policy" className="underline text-white/70 hover:text-white" target="_blank">
                    {t("سياسة الخصوصية", "Privacy Policy")}
                  </Link>
                  {t(" و", " and ")}{" "}
                  <Link to="/terms" className="underline text-white/70 hover:text-white" target="_blank">
                    {t("الشروط والأحكام", "Terms & Conditions")}
                  </Link>
                </p>
              </>
            ) : step === "email_login" ? (
              <>
                {/* Back button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep("phone")}
                  className="text-white/70 hover:text-white hover:bg-white/10 -ms-3"
                >
                  <ArrowLeft className="h-4 w-4 me-2 rtl:rotate-180" />
                  {t("رجوع", "Back")}
                </Button>

                {/* Header */}
                <div className="space-y-2 w-full !text-center">
                  <h1 className="text-2xl font-bold text-white !text-center">
                    {t("تسجيل الدخول بالبريد الإلكتروني", "Login with Email")}
                  </h1>
                  <p className="text-white/60 text-sm !text-center">
                    {t("أدخل بريدك الإلكتروني وكلمة المرور", "Enter your email and password")}
                  </p>
                </div>

                {/* Email input */}
                <div className="space-y-2 w-full">
                  <label className="text-sm font-medium text-white">
                    {t("البريد الإلكتروني", "Email")}
                  </label>
                  <Input
                    type="email"
                    value={emailLogin}
                    onChange={(e) => setEmailLogin(e.target.value)}
                    placeholder="ahmed@example.com"
                    dir="ltr"
                    disabled={isLoading}
                    autoFocus
                    className="bg-white/10 border-white/20 text-white placeholder-white/40"
                  />
                </div>

                {/* Password input */}
                <div className="space-y-2 w-full">
                  <label className="text-sm font-medium text-white">
                    {t("كلمة المرور", "Password")}
                  </label>
                  <Input
                    type="password"
                    value={passwordLogin}
                    onChange={(e) => setPasswordLogin(e.target.value)}
                    placeholder="••••••••"
                    dir="ltr"
                    disabled={isLoading}
                    onKeyDown={(e) => e.key === "Enter" && handleEmailLogin()}
                    className="bg-white/10 border-white/20 text-white placeholder-white/40"
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setStep("forgot_password")}
                      className="text-xs text-[#a46ef0] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      {t("نسيت كلمة المرور؟", "Forgot password?")}
                    </button>
                  </div>
                </div>

                {/* Login button */}
                <Button
                  onClick={handleEmailLogin}
                  className="w-full bg-[#791FDA] hover:bg-[#6a18c7] text-white"
                  disabled={isLoading || !emailLogin || !passwordLogin}
                  size="lg"
                  data-testid="email-login-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="me-2 h-4 w-4 animate-spin" />
                      {t("جاري تسجيل الدخول...", "Signing in...")}
                    </>
                  ) : (
                    t("تسجيل الدخول", "Login")
                  )}
                </Button>

                {/* PDPL Art. 5–6 — passive informational notice (not a blocking checkbox) */}
                <p className="text-xs text-white/50 leading-relaxed text-start w-full">
                  {t("بالمتابعة، أنت توافق على ", "By continuing, you agree to our ")}{" "}
                  <Link to="/privacy-policy" className="underline text-white/70 hover:text-white" target="_blank">
                    {t("سياسة الخصوصية", "Privacy Policy")}
                  </Link>
                  {t(" و", " and ")}{" "}
                  <Link to="/terms" className="underline text-white/70 hover:text-white" target="_blank">
                    {t("الشروط والأحكام", "Terms & Conditions")}
                  </Link>
                </p>

                {/* Back to phone */}
                <button
                  type="button"
                  onClick={() => setStep("phone")}
                  className="text-sm text-[#791FDA] hover:underline mx-auto"
                >
                  {t("تسجيل الدخول برقم الجوال", "Login with phone number")}
                </button>
              </>
            ) : step === "forgot_password" ? (
              <ForgotPasswordForm initialEmail={emailLogin} onBack={() => setStep("email_login")} />
            ) : step === "new_user" ? (
              <>
                {/* Back button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep("phone")}
                  className="text-white/70 hover:text-white hover:bg-white/10 -ms-3"
                >
                  <ArrowLeft className="h-4 w-4 me-2 rtl:rotate-180" />
                  {t("رجوع", "Back")}
                </Button>

                {/* Header */}
                <div className="space-y-2 w-full !text-center">
                  <h1 className="text-2xl font-bold text-white !text-center" data-testid="not-registered">
                    {t("رقم غير مسجل", "Number Not Registered")}
                  </h1>
                  <p className="text-white/60 text-sm !text-center">
                    {t(
                      "هذا الرقم غير مرتبط بحساب. هل أنت مالك عقار؟",
                      "This number is not linked to an account. Are you a property owner?"
                    )}
                  </p>
                </div>

                {/* PDPL Art. 5–6 — the visitor here is ANONYMOUS (their phone is
                    not registered), so the box only gates the register link.
                    The durable consent rows (incl. the marketing choice) are
                    written by OwnerAuth after OTP, against a real user. */}
                <ConsentCheckboxes
                  value={{ terms: signupConsentChecked, marketing: false }}
                  onChange={(v) => setSignupConsentChecked(v.terms)}
                  tone="dark"
                  showMarketing={false}
                  idPrefix="consent-signup"
                />

                {/* Register as owner — disabled until consent checked */}
                <Link
                  to={signupConsentChecked ? "/owner-auth" : "#"}
                  aria-disabled={!signupConsentChecked}
                  onClick={(e) => { if (!signupConsentChecked) e.preventDefault(); }}
                  className={`flex items-center justify-center gap-2 w-full rounded-md px-4 py-3 text-sm font-medium transition-colors ${
                    signupConsentChecked
                      ? "bg-[#791FDA] hover:bg-[#6a18c7] text-white"
                      : "bg-[#791FDA]/40 text-white/50 cursor-not-allowed pointer-events-none"
                  }`}
                >
                  <Building2 className="h-4 w-4" />
                  {t("التسجيل كمالك عقار", "Register as Property Owner")}
                </Link>

                {/* Invitation hint */}
                <p className="text-center text-xs text-white/40 leading-relaxed">
                  {t(
                    "هل لديك رابط دعوة؟ استخدمه للوصول إلى حسابك",
                    "Have an invitation link? Use it to access your account"
                  )}
                </p>
              </>
            ) : (
              <>
                {/* Back button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="text-white/70 hover:text-white hover:bg-white/10 -ms-3"
                  data-testid="auth-back"
                >
                  <ArrowLeft className="h-4 w-4 me-2 rtl:rotate-180" />
                  {t("رجوع", "Back")}
                </Button>

                {/* Header */}
                <div className="space-y-2">
                  <h1 className="text-2xl font-bold text-white" data-testid="otp-heading">
                    {t("أدخل رمز التحقق", "Enter Verification Code")}
                  </h1>
                  <p className="text-white/60 text-sm">
                    {t("تم إرسال رمز التحقق إلى رقم جوالك", "Verification code sent to your phone")}
                  </p>
                  <p className="text-sm font-medium text-[#791FDA]" dir="ltr">
                    {phoneNumber}
                  </p>
                </div>

                {/* OTP field */}
                <div className="space-y-2" data-testid="otp-input">
                  <label className="text-sm font-medium text-white">
                    {t("رمز الدخول", "Verification Code")} <span className="text-red-400">*</span>
                  </label>
                  <OtpInput
                    key={otpKey}
                    value={otp}
                    onChange={setOtp}
                    onComplete={(v) => handleVerifyOtp(v)}
                    disabled={isLoading}
                    autoFocus
                    devOtp={devOtp}
                  />
                </div>

                {/* Verify button */}
                <Button
                  onClick={() => handleVerifyOtp()}
                  className="w-full bg-[#791FDA] hover:bg-[#6a18c7] text-white"
                  disabled={isLoading || otp.length !== OTP_LENGTH}
                  size="lg"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="me-2 h-4 w-4 animate-spin" />
                      {t("جاري التحقق...", "Verifying...")}
                    </>
                  ) : (
                    t("تسجيل الدخول", "Login")
                  )}
                </Button>

                {/* Resend */}
                <div className="text-center">
                  {resendTimer > 0 ? (
                    <p className="text-sm text-white/50">
                      {t("إعادة الإرسال خلال", "Resend in")} {resendTimer} {t("ثانية", "seconds")}
                    </p>
                  ) : (
                    <Button
                      variant="link"
                      onClick={handleResendOtp}
                      disabled={isLoading}
                      className="text-[#791FDA]"
                    >
                      {t("إعادة إرسال الرمز", "Resend Code")}
                    </Button>
                  )}
                </div>

                {/* Dev OTP display */}
                {devOtp && (
                  <p className="text-xs text-center text-amber-400" dir="ltr">
                    Dev OTP: <span className="font-mono font-bold">{devOtp}</span>
                  </p>
                )}
              </>
            )}
      </AuthShell>

      {/* Nafath Verification Modal */}
      <NafathVerificationModal
        open={step === "nafath"}
        onOpenChange={() => {
          // Mandatory — cannot dismiss
        }}
        userId={loggedInUserId}
        companyId={loggedInCompanyId}
        onVerified={handleNafathVerified}
      />
    </>
  );
}
